import * as React from "react";
import {
    Box,
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    Button,
} from "@mui/material";
import { LOCAL_CONSTANT } from "../Const";
import {
    tableStyle,
    tableHeaderStyle,
    tableCellStyle,
    tableHeaderSmallStyle,
    tableCellSmallStyle,
    tableHeaderDateStyle,
    tableCellDateStyle,
    tableCellWhiteSpace,
    tableHeaderNameStyle,
    tableCellNameStyle,
} from "../components/ChangeHistoryTableStyle";
import { apiRequest } from "../../api/ApiCall";
import { useUtilityContext } from "../../utility-provider";
import ChangeHistoryTableComment from "../components/ChangeHistoryTableComment";
import FilterModal from "../components/FilterModal";
import { FilterCriteria, initFilterCriteriaObj, FilterOptions, initFilterOptionsObj, ChangeHistoryItem, initChangeHistoryList } from "../../data/type";
import dayjs from "dayjs";
import {
    changeHistoryTableName,
    changeHistoryColumnName,
    changeHistorySelecterDisplay,
    addSortData,
    generateFilterOptions,
    applyFilter,
    groupChangeHistoryByItemHierarchy,
    getItemGroupSummary,
    processCommentsForCombination,
} from "../components/ChangeHistoryFunc";
import { useUserContext } from "../../user-provider";
import { decryptQueryParam } from '../../utility/util';
import { usePlanAreaSearchByIdForHeader } from "../Common";

export interface ItemChangeHistoryProps {
    props: {
        width: number;
        height: number;
    };
}


const ItemChangeHistory: React.FC<ItemChangeHistoryProps> = ({ props }) => {
    const utilityCtx = useUtilityContext();
    //URLパラメータ
    const [planAreaId, setPlanAreaId] = React.useState("");
    const [branch, setBranch] = React.useState("");
    // 最新のブランチ
    const [latestBranch, setLatestBranch] = React.useState<string>("");

    const [changeHistory, setChangeHistory] = React.useState<ChangeHistoryItem[]>(initChangeHistoryList);
    const [comment, setComment] = React.useState<any[]>([]);
    const [tableNameMaster, setTableNameMaster] = React.useState<any>([]);
    const [commentOpen, setCommentOpen] = React.useState<boolean>(false);
    const [codeMaster, setCodeMaster] = React.useState<any>();
    const [userType, setUserType] = React.useState<string>("");
    
    // フィルタ関連の状態管理
    const [filterModalOpen, setFilterModalOpen] = React.useState<boolean>(false); // フィルタモーダルの開閉状態
    const [originalChangeHistory, setOriginalChangeHistory] = React.useState<ChangeHistoryItem[]>(initChangeHistoryList); // フィルタ前の元データ
    const [filterCriteria, setFilterCriteria] = React.useState<FilterCriteria>(initFilterCriteriaObj);  // 現在のフィルタ条件
    const [filterOptions, setFilterOptions] = React.useState<FilterOptions>(initFilterOptionsObj); // フィルタ選択肢
    
    // ページグループ化関連の状態管理
    const [expandedBranches, setExpandedBranches] = React.useState<{[key: string]: boolean}>({}); // 枝グループの展開状態
    const [expandedTables, setExpandedTables] = React.useState<{[key: string]: boolean}>({}); // テーブル論理名グループの展開状態（商圏ページ用）
    const [expandedItems, setExpandedItems] = React.useState<{[key: string]: boolean}>({}); // 項目グループの展開状態
    const [isAllExpanded, setIsAllExpanded] = React.useState<boolean>(false); // 全展開状態の管理
    
    const user = useUserContext();
    const updateHeader = usePlanAreaSearchByIdForHeader();


    React.useEffect(() => {
        //暗号化されたクエリパラメータを復号化
        const search = window.location.search;
        const urlParam = search.substring(1);
        decryptQueryParam(urlParam).then((res) => {
            setPlanAreaId(res[0]);
            setBranch(res[1]);
        }).catch((e:any) => {
            console.log(e);
            if (utilityCtx.hideSpinner) {
                utilityCtx.hideSpinner();
            }
        });
        const tableNameMasterObj = localStorage.getItem("tableNameMaster");
        if (tableNameMasterObj) {
            setTableNameMaster(JSON.parse(tableNameMasterObj));
        }
        const res: any = localStorage.getItem("codeMaster");
        if (res) {
            const d = JSON.parse(res);
            setCodeMaster(d);
        }
    }, []);

    React.useEffect(()=>{
        if(planAreaId !== ""){
            callLatestBranch();
            callChangeHistory();
            // 候補地情報をRedux storeに設定（共有関数を使用）
            if (user.userData) {
                updateHeader(planAreaId, user.userData);
            }
        }
    },[planAreaId, user.userData]);

    const callLatestBranch = async () => {
        const params = {
            mode: "regist",
            endPoint: "/location/Reception/v1/latestBranch",
            query: {
                plan_area_id: planAreaId,
            },
        };
        try {
            const res = await apiRequest(params);
            if (res.data && res.data.dataList && res.data.dataList.length > 0) {
                try {
                    const branchData = JSON.parse(res.data.dataList[0]);
                    if (branchData.latest_branch) {
                        setLatestBranch(branchData.latest_branch);
                    }
                } catch (parseError) {
                    console.log("Latest branch JSON parse error:", parseError);
                }
            }
        } catch (e) {
            console.log("Latest branch API error:", e);
        }
    };

    const callChangeHistory = () => {
        // 変更履歴
        const params = {
            mode: "regist",
            endPoint: "/location/system/v1/get-log",
            query: {
                plan_area_id: planAreaId,
                log_type: "rfc-rr",
            },
        };
        // コメント
        const commentParams = {
            mode: "regist",
            endPoint: "/location/system/v1/get-rfc-rr-comment",
            query: {
                plan_area_id: planAreaId,
            },
        };
        if (utilityCtx.showSpinner) {
            utilityCtx.showSpinner();
        }
        (async () => {
            try {
                const res = await apiRequest(params);
                // 変更履歴の表示用に事前計算して追加
                const preCalculatedData = res.data
                    .filter((item: any) => item.target_table && !item.target_table.includes("RR内部用")) // RR内部用ページは変更履歴に表示しない
                    .map((item: any) => ({
                        ...item,
                        tableLogicName: changeHistoryTableName(tableNameMaster, item.target_table, item),
                        columnLogicName: changeHistoryColumnName(tableNameMaster, item.target_column, item),
                        beforeValueDisplay: changeHistorySelecterDisplay(codeMaster, item.target_column, item.target_before_value),
                        afterValueDisplay: changeHistorySelecterDisplay(codeMaster, item.target_column, item.target_after_value)
                    }));
                // ページグループ化とソートのためのpage_sort値とcolumn_sort値を付与（事前計算されたデータを使用）
                const enhancedData = addSortData(preCalculatedData, codeMaster, tableNameMaster);
                
                // コメントデータを取得して処理
                const comment_res = await apiRequest(commentParams);
                setComment(comment_res.data);
                
                // コメントデータを変更履歴と同じ構造に変換
                const processedComments = processCommentsForCombination(comment_res.data, codeMaster);
                
                // 変更履歴とコメントデータを結合
                const combinedData = [...enhancedData, ...processedComments];
                
                setOriginalChangeHistory(combinedData);
                setChangeHistory(combinedData);
                const filterOpts = generateFilterOptions(combinedData, codeMaster);
                setFilterOptions(filterOpts);
                if (utilityCtx.hideSpinner) {
                    utilityCtx.hideSpinner();
                }
            } catch (e) {
                console.log(e);
                if (utilityCtx.hideSpinner) {
                    utilityCtx.hideSpinner();
                }
            }
        })();
    };

    const change_details = (log_type: string) => {
        const change_details_list = [
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.ITEM_CHANGE, // "0"
            "",
            "",
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.CONFLICT_CVS_CHANGE, // "3"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.CONFLICT_SMDS_CHANGE, // "4"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.IMPACT_INTERSECTION_CHANGE, // "5"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.SALES_FORECAST_SUMMARY_TABLE_CHANGE, // "6"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.ADDITIONAL_SURVEY_CHANGE, // "7"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.OTHER_DOCUMENT_CHANGE, // "8"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.RR_INTERIOR_CHANGE, // "9"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.CURRENT_STATUS_PHOTO_CHANGE, // "10"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.TRAFFIC_VOLUME_CHANGE, // "11"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.FIVEM_EXTRA_CALCULATION_BASIS_CHANGE, // "12"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.TRADE_AREA_CUT_CHANGE, // 13
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.IMPACT_ASSESSMENT_CHANGE, // 14
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.MAP_CHANGE, // 15
        ];

        return change_details_list[Number(log_type)];
    };

    const handleCommentOpen = () => {
        setCommentOpen(true);
    };

    const handleSaveComment = () => {
        setCommentOpen(false);
        callChangeHistory();
    };

    const handleCanCelComment = () => {
        setCommentOpen(false);
    };

    const displayTime = (date: string) => {
        const time = new Date(date);
        time.setHours(time.getHours() + 9);
        return dayjs(time).format("YYYY-MM-DD HH:mm:ss");
    };


    // フィルタを適用
    const handleApplyFilter = (filters: FilterCriteria) => {
        const filteredData = applyFilter(originalChangeHistory, filters);
        setChangeHistory(filteredData);
        setFilterCriteria(filters);
    };

    // フィルタモーダルを開く
    const handleFilterOpen = () => {
        setFilterModalOpen(true);
    };

    // フィルタモーダルを閉じる
    const handleFilterClose = () => {
        setFilterModalOpen(false);
    };

    // ページグループの展開/折りたたみ
    const handlePageGroupClick = (branchKey: string, pageName: string) => {
        const key = `${branchKey}_${pageName}`;
        setExpandedTables(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // テーブルグループの展開/折りたたみ（商圏・通行量・競合店・影響度ページ用の4階層構造で特別な処理を含む）
    const handleTableExpandGroupClick = (branchKey: string, pageName: string, tableLogicName: string) => {
        const key = `${branchKey}_${pageName}_${tableLogicName}`;
        setExpandedTables(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // 項目グループの展開/折りたたみ
    const handleItemGroupClick = (branchKey: string, pageName: string, itemName: string, tableLogicName?: string) => {
        const key = tableLogicName 
            ? `${branchKey}_${pageName}_${tableLogicName}_${itemName}` // 4階層ページ（商圏・通行量・競合店・影響度）の場合
            : `${branchKey}_${pageName}_${itemName}`; // 3階層ページ（通常）の場合
        setExpandedItems(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // 全展開/全折りたたみのトグル機能
    const handleToggleExpandAll = () => {
        if (Object.keys(groupedData).length === 0) return;

        if (isAllExpanded) {
            // 全折りたたみ
            setExpandedBranches({});
            setExpandedTables({});
            setExpandedItems({});
            setIsAllExpanded(false);
        } else {
            // 全展開
            const newExpandedBranches: {[key: string]: boolean} = {};
            const newExpandedTables: {[key: string]: boolean} = {};
            
            Object.entries(groupedData).forEach(([branchKey, pages]) => {
                // 第1階層（枝レベル）を展開
                newExpandedBranches[branchKey] = true;
                
                Object.entries(pages as any).forEach(([pageName, pageContent]) => {
                    // 第2階層（ページレベル）を展開
                    const pageExpandKey = `${branchKey}_${pageName}`;
                    newExpandedTables[pageExpandKey] = true;
                    
                    // 4階層ページ（通行量、商圏、競合店、影響度）の場合は第3階層（テーブル論理名レベル）も展開
                    if (pageName === "商圏" || pageName === "通行量" || 
                        pageName === "競合店（CVS）" || pageName === "競合店（SMDS）" || 
                        pageName === "影響度") {
                        if (pageContent && typeof pageContent === "object") {
                            Object.keys(pageContent).forEach(tableLogicName => {
                                const tableExpandKey = `${branchKey}_${pageName}_${tableLogicName}`;
                                newExpandedTables[tableExpandKey] = true;
                            });
                        }
                    }
                });
            });
            
            setExpandedBranches(newExpandedBranches);
            setExpandedTables(newExpandedTables);
            setIsAllExpanded(true);
        }
    };

    //ユーザー取得
    React.useEffect(() => {
        if (JSON.parse(user.userData || "null") !== null) {
            setUserType(JSON.parse(user.userData || "null").user_type);
        }
    }, [user]);

    // React.useMemoを使用してデータを階層構造でグループ化
    // groupChangeHistoryByItemHierarchy関数による統合処理：
    // - 商圏ページ: 4階層（枝 > ページ > テーブル論理名 + 町名付き > 項目（trade_area_seq昇順 → column_sort昇順））
    // - 通行量ページ: 4階層（枝 > ページ > テーブル論理名 + traffic_type + traffic_type_seq付き > 項目（traffic_type昇順 → traffic_type_seq有無 → traffic_type_seq昇順 → column_sort昇順））
    // - 競合店ページ: 4階層（枝 > ページ > テーブル論理名 + 店舗名付き > 項目（column_sort昇順））
    // - 影響度ページ: 4階層（枝 > ページ > 店舗名キー > 項目（location_impact優先 → location_impact_counterはroute_seq順序））
    //   * 影響度ページの特殊構造：
    //     - location_impact: "影響度\n店舗名" （基本項目）
    //     - location_impact_counter: "影響度\n店舗名\nルート名\n交差点名" （カウンター項目）
    //   * ソート順序: 基本項目 → カウンター項目（route_seq順）
    // - 通常ページ: 3階層（枝 > ページ > 項目（column_sort昇順））
    // 依存配列: [changeHistory, tableNameMaster, codeMaster] - これらが変更された時のみ再計算
    const groupedData = React.useMemo(() => {
        if (changeHistory.length === 0 || tableNameMaster.length === 0) {
            return {};
        }
        
        return groupChangeHistoryByItemHierarchy(changeHistory, tableNameMaster, codeMaster);
    }, [changeHistory, tableNameMaster, codeMaster]);


    return (
        <Box sx={{ width: "100%" }}>
            <Grid container>
                <Grid item xs={12} sx={{ height: "48px" }} />
            </Grid>
            <Grid container direction={"row"} sx={{ position: "fixed" }}>
                <Grid item sx={{ width: "55px", height: props.height }} />
                <Grid
                    item
                    sx={{ width: props.width, height: props.height, overflowY: "scroll" }}>
                    <Grid item sx={{ marginTop: "10px", marginLeft: "10px", marginBottom: "10px" }}>
                        <Box sx={{ 
                            display: "flex", 
                            alignItems: "center",
                            minHeight: "48px",
                            paddingY: "8px"
                        }}>
                            <Typography sx={{ display: "flex", alignItems: "center" }}>
                                {LOCAL_CONSTANT.LABEL.RFC_ITEM_CHANGE_HISTORY}
                            </Typography>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handleFilterOpen}
                                sx={{ 
                                    margin: "0px 10px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {LOCAL_CONSTANT.LABEL.FILTER}
                            </Button>
                            {/* RFCとRRときのみコメント登録ボタンを表示 */}
                            {LOCAL_CONSTANT.USER_TYPE.RFC_RR.includes(userType) && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    onClick={handleCommentOpen}
                                    sx={{ 
                                        margin: "0px 10px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {LOCAL_CONSTANT.LABEL.REGIST_COMMENT}
                                </Button>
                            )}
                            {/* 全展開/全折りたたみボタン */}
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleToggleExpandAll}
                                sx={{ 
                                    margin: "0px 10px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {isAllExpanded ? "全折りたたみ" : "全展開"}
                            </Button>
                        </Box>
                    </Grid>
                    {/* RFC項目変更履歴テーブル */}
                    <TableContainer sx={{ height: "80%", minHeight: "80%", maxHeight: "80%", overflowY: "auto" }}>
                        <Table stickyHeader sx={tableStyle}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={tableHeaderDateStyle}>{LOCAL_CONSTANT.LABEL.DATE}</TableCell>
                                    <TableCell sx={tableHeaderNameStyle}>{LOCAL_CONSTANT.LABEL.EDITOR}</TableCell>
                                    <TableCell sx={tableHeaderSmallStyle}>{LOCAL_CONSTANT.LABEL.BRANCH}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.PAGE}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.ITEM}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.CHANGE_BEFORE}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.AFTER_CHANGE}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.CHANGE_DETAILS}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(groupedData).map(([branchKey, pages]) => {
                                    // 枝レベルのコメントをフィルタリング
                                    const branchNumber = branchKey.replace(/^枝/, ''); // "枝1" → "1"
                                    return (
                                        <React.Fragment key={`branch-${branchKey}`}>
                                            {Object.entries(pages as any).map(([pageName, pageContent]) => {
                                                const pageExpandKey = `${branchKey}_${pageName}`;
                                                const isPageExpanded = expandedTables[pageExpandKey];
                                                
                                                // ページグループヘッダー行用の最新の項目を取得
                                                let pageFirstItem = null;
                                                let pageLatestPublishTime = new Date(0);
                                                
                                                if (typeof pageContent === "object" && pageContent !== null) {
                                                    for (const itemOrTableGroup of Object.values(pageContent)) {
                                                        if (Array.isArray(itemOrTableGroup)) {
                                                            // 3階層の場合（直接項目配列）
                                                            for (const item of itemOrTableGroup) {
                                                                const itemDate = new Date(item.publish_time);
                                                                if (itemDate > pageLatestPublishTime) {
                                                                    pageLatestPublishTime = itemDate;
                                                                    pageFirstItem = item;
                                                                }
                                                            }
                                                        } else if (typeof itemOrTableGroup === "object") {
                                                            // 4階層の場合（テーブルグループ内の項目配列）
                                                            for (const itemGroup of Object.values(itemOrTableGroup)) {
                                                                if (Array.isArray(itemGroup)) {
                                                                    for (const item of itemGroup) {
                                                                        const itemDate = new Date(item.publish_time);
                                                                        if (itemDate > pageLatestPublishTime) {
                                                                            pageLatestPublishTime = itemDate;
                                                                            pageFirstItem = item;
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                                
                                                // ページレベルのコメントをフィルタリング
                                                const pageComments = comment.filter((commentItem: any) => {
                                                    return commentItem.target_table === pageName && commentItem.branch === branchNumber;
                                                });
                                                
                                                // 最新のコメントを取得
                                                const pageLatestComment = pageComments.length > 0 
                                                    ? pageComments.sort((a, b) => new Date(b.publish_time).getTime() - new Date(a.publish_time).getTime())[0]
                                                    : null;
                                                
                                                // 商圏ページかどうかを判定
                                                const isTradeAreaPage = pageName === "商圏";
                                                // 通行量ページかどうかを判定
                                                const isTrafficPage = pageName === "通行量";
                                                // 競合店の判定を細分化
                                                const isConflictStorePage = pageName === "競合店（CVS）" || pageName === "競合店（SMDS）";
                                                // 影響度ページかどうかを判定
                                                const isImpactPage = pageName === "影響度";
                                                
                                                return (
                                                    <React.Fragment key={`page-${pageExpandKey}`}>
                                                        {/* ページグループヘッダー行 */}
                                                        <TableRow>
                                                            <TableCell sx={tableCellDateStyle}>
                                                                {pageFirstItem ? displayTime(pageFirstItem.publish_time) : ""}
                                                            </TableCell>
                                                            <TableCell sx={tableCellNameStyle}>
                                                                {pageFirstItem ? pageFirstItem.update_name : ""}
                                                            </TableCell>
                                                            <TableCell sx={tableCellSmallStyle}>
                                                                {pageFirstItem ? pageFirstItem.branch : ""}
                                                            </TableCell>
                                                            <TableCell 
                                                                sx={{
                                                                    ...tableCellWhiteSpace,
                                                                    backgroundColor: "#FFFFCC",
                                                                    cursor: "pointer"
                                                                }}
                                                                onClick={() => handlePageGroupClick(branchKey, pageName)}
                                                            >
                                                                {pageName} {isPageExpanded ? "▲" : "▼"}
                                                            </TableCell>
                                                            <TableCell sx={tableCellWhiteSpace}></TableCell>
                                                            <TableCell sx={tableCellStyle}></TableCell>
                                                            <TableCell sx={tableCellStyle}></TableCell>
                                                            <TableCell sx={tableCellStyle}>
                                                                {pageLatestComment ? pageLatestComment.comment : ""}
                                                            </TableCell>
                                                        </TableRow>
                                                        
                                                        {/* ページ展開時の内容 */}
                                                        {isPageExpanded && (() => {
                                                            if (isTradeAreaPage || isTrafficPage || isConflictStorePage || isImpactPage) {
                                                                // 4階層構造: 枝 > ページ > テーブル論理名 > 項目
                                                                const tableGroups: {[tableLogicName: string]: {[itemName: string]: any[]}} = pageContent as any;
                                                                
                                                                // コメント項目と通常項目を分離
                                                                const commentItems: {[itemName: string]: any[]} = {};
                                                                const regularTableGroups: {[tableLogicName: string]: {[itemName: string]: any[]}} = {};
                                                                
                                                                Object.entries(tableGroups).forEach(([tableLogicName, itemGroups]) => {
                                                                    Object.entries(itemGroups).forEach(([itemName, items]) => {
                                                                        const firstItem = Array.isArray(items) && items.length > 0 ? items[0] : null;
                                                                        if (firstItem && firstItem.target_column === "コメント") {
                                                                            // コメント項目：直接詳細レコードとして表示するため分離
                                                                            commentItems[itemName] = items;
                                                                        } else {
                                                                            // 通常項目：テーブルグループで表示
                                                                            if (!regularTableGroups[tableLogicName]) {
                                                                                regularTableGroups[tableLogicName] = {};
                                                                            }
                                                                            regularTableGroups[tableLogicName][itemName] = items;
                                                                        }
                                                                    });
                                                                });
                                                                
                                                                return (
                                                                    <React.Fragment>
                                                                        {/* コメント項目：詳細レコードを直接表示 */}
                                                                        {Object.entries(commentItems).map(([itemName, items]) => {
                                                                            return (
                                                                                <React.Fragment key={`comment-item-${itemName}`}>
                                                                                    {Array.isArray(items) && items.map((row: any, index: number) => (
                                                                                        <TableRow key={`${row.primaryKey}-time-${row.publish_time}-${index}`}>
                                                                                            <TableCell sx={tableCellDateStyle}>{displayTime(row.publish_time)}</TableCell>
                                                                                            <TableCell sx={tableCellNameStyle}>{row.update_name}</TableCell>
                                                                                            <TableCell sx={tableCellSmallStyle}>{row.branch}</TableCell>
                                                                                            <TableCell sx={tableCellWhiteSpace}>{row.tableLogicName}</TableCell>
                                                                                            <TableCell sx={tableCellWhiteSpace}>{row.columnLogicName}</TableCell>
                                                                                            <TableCell sx={tableCellStyle}></TableCell>
                                                                                            <TableCell sx={tableCellStyle}></TableCell>
                                                                                            <TableCell sx={tableCellStyle}>{row.afterValueDisplay}</TableCell>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </React.Fragment>
                                                                            );
                                                                        })}
                                                                        
                                                                        {/* 通常項目：4階層表示（テーブル論理名グループ） */}
                                                                        {Object.entries(regularTableGroups).map(([tableLogicName, itemGroups]) => {
                                                                            const tableExpandKey = `${branchKey}_${pageName}_${tableLogicName}`;
                                                                            const isTableExpanded = expandedTables[tableExpandKey];
                                                                            
                                                                            // テーブル論理名内で日付が最新の項目を取得
                                                                            let tableFirstItem = null;
                                                                            let tableLatestPublishTime = new Date(0);
                                                                            
                                                                            for (const itemGroup of Object.values(itemGroups)) {
                                                                                if (Array.isArray(itemGroup) && itemGroup.length > 0) {
                                                                                    for (const item of itemGroup) {
                                                                                        const itemDate = new Date(item.publish_time);
                                                                                        if (itemDate > tableLatestPublishTime) {
                                                                                            tableLatestPublishTime = itemDate;
                                                                                            tableFirstItem = item;
                                                                                        }
                                                                                    }
                                                                                }
                                                                            }
                                                                            
                                                                            return (
                                                                                <React.Fragment key={`table-expand-${tableExpandKey}`}>
                                                                                    {/* テーブル論理名グループヘッダー行 */}
                                                                                    <TableRow>
                                                                                        <TableCell sx={tableCellDateStyle}>
                                                                                            {tableFirstItem ? displayTime(tableFirstItem.publish_time) : ""}
                                                                                        </TableCell>
                                                                                        <TableCell sx={tableCellNameStyle}>
                                                                                            {tableFirstItem ? tableFirstItem.update_name : ""}
                                                                                        </TableCell>
                                                                                        <TableCell sx={tableCellSmallStyle}>
                                                                                            {tableFirstItem ? tableFirstItem.branch : ""}
                                                                                        </TableCell>
                                                                                        <TableCell 
                                                                                            sx={{
                                                                                                ...tableCellWhiteSpace,
                                                                                                backgroundColor: "#C0FFC0",
                                                                                                cursor: "pointer"
                                                                                            }}
                                                                                            onClick={() => handleTableExpandGroupClick(branchKey, pageName, tableLogicName)}
                                                                                        >
                                                                                            {tableLogicName} {isTableExpanded ? "▲" : "▼"}
                                                                                        </TableCell>
                                                                                        <TableCell sx={tableCellWhiteSpace}></TableCell>
                                                                                        <TableCell sx={tableCellStyle}></TableCell>
                                                                                        <TableCell sx={tableCellStyle}></TableCell>
                                                                                        <TableCell sx={tableCellStyle}></TableCell>
                                                                                    </TableRow>
                                                                                    
                                                                                    {/* テーブル論理名展開時の項目グループ */}
                                                                                    {isTableExpanded && Object.entries(itemGroups).map(([itemName, items]) => {
                                                                                        const itemExpandKey = `${branchKey}_${pageName}_${tableLogicName}_${itemName}`;
                                                                                        const isItemExpanded = expandedItems[itemExpandKey];
                                                                                        const itemFirstRecord = Array.isArray(items) && items.length > 0 ? items[0] : null;
                                                                                        
                                                                                        // 項目グループの集約データを取得
                                                                                        const itemSummary = getItemGroupSummary(items);
                                                                                        
                                                                                        return (
                                                                                            <React.Fragment key={`item-${itemExpandKey}`}>
                                                                                                {/* 項目グループヘッダー行 */}
                                                                                                <TableRow>
                                                                                                    <TableCell sx={tableCellDateStyle}>
                                                                                                        {itemFirstRecord ? displayTime(itemFirstRecord.publish_time) : ""}
                                                                                                    </TableCell>
                                                                                                    <TableCell sx={tableCellNameStyle}>
                                                                                                        {itemFirstRecord ? itemFirstRecord.update_name : ""}
                                                                                                    </TableCell>
                                                                                                    <TableCell sx={tableCellSmallStyle}>
                                                                                                        {itemFirstRecord ? itemFirstRecord.branch : ""}
                                                                                                    </TableCell>
                                                                                                    <TableCell sx={tableCellWhiteSpace}>
                                                                                                        {itemFirstRecord ? itemFirstRecord.tableLogicName : ""}
                                                                                                    </TableCell>
                                                                                                    <TableCell 
                                                                                                        sx={{
                                                                                                            ...tableCellWhiteSpace,
                                                                                                            backgroundColor: "#CCFFFF",
                                                                                                            cursor: "pointer"
                                                                                                        }}
                                                                                                        onClick={() => handleItemGroupClick(branchKey, pageName, itemName, tableLogicName)}
                                                                                                    >
                                                                                                        {itemName} {isItemExpanded ? "▲" : "▼"}
                                                                                                    </TableCell>
                                                                                                    <TableCell sx={tableCellStyle}>
                                                                                                        {itemSummary ? itemSummary.oldestRecord.beforeValueDisplay : ""}
                                                                                                    </TableCell>
                                                                                                    <TableCell sx={tableCellStyle}>
                                                                                                        {itemSummary ? itemSummary.newestRecord.afterValueDisplay : ""}
                                                                                                    </TableCell>
                                                                                                    <TableCell sx={tableCellStyle}>
                                                                                                        {itemSummary ? change_details(itemSummary.latestRecord.log_type) : ""}
                                                                                                    </TableCell>
                                                                                                </TableRow>
                                                                                                
                                                                                                {/* 項目展開時の詳細レコード */}
                                                                                                {isItemExpanded && Array.isArray(items) && items.map((row: any, index: number) => (
                                                                                                    <TableRow key={`${row.primaryKey}-time-${row.publish_time}-${index}`}>
                                                                                                        <TableCell sx={tableCellDateStyle}>{displayTime(row.publish_time)}</TableCell>
                                                                                                        <TableCell sx={tableCellNameStyle}>{row.update_name}</TableCell>
                                                                                                        <TableCell sx={tableCellSmallStyle}>{row.branch}</TableCell>
                                                                                                        <TableCell sx={tableCellWhiteSpace}>{row.tableLogicName}</TableCell>
                                                                                                        <TableCell sx={tableCellWhiteSpace}>{row.columnLogicName}</TableCell>
                                                                                                        <TableCell sx={tableCellStyle}>{row.beforeValueDisplay}</TableCell>
                                                                                                        <TableCell sx={tableCellStyle}>{row.afterValueDisplay}</TableCell>
                                                                                                        <TableCell sx={tableCellStyle}>{change_details(row.log_type)}</TableCell>
                                                                                                    </TableRow>
                                                                                                ))}
                                                                                            </React.Fragment>
                                                                                        );
                                                                                    })}
                                                                                </React.Fragment>
                                                                            );
                                                                        })}
                                                                    </React.Fragment>
                                                                );
                                                            } else {
                                                                // 3階層構造: 枝 > ページ > 項目
                                                                const itemGroups = pageContent as {[itemName: string]: any[]};
                                                                
                                                                // コメント項目と通常項目を分離
                                                                const commentItems: {[itemName: string]: any[]} = {};
                                                                const regularItems: {[itemName: string]: any[]} = {};
                                                                
                                                                Object.entries(itemGroups).forEach(([itemName, items]) => {
                                                                    const firstItem = Array.isArray(items) && items.length > 0 ? items[0] : null;
                                                                    if (firstItem && firstItem.target_column === "コメント") {
                                                                        // コメント項目：直接詳細レコードとして表示するため分離
                                                                        commentItems[itemName] = items;
                                                                    } else {
                                                                        // 通常項目：青い行で表示
                                                                        regularItems[itemName] = items;
                                                                    }
                                                                });
                                                                
                                                                return (
                                                                    <React.Fragment>
                                                                        {/* コメント項目：詳細レコードを直接表示 */}
                                                                        {Object.entries(commentItems).map(([itemName, items]) => {
                                                                            return (
                                                                                <React.Fragment key={`comment-item-${itemName}`}>
                                                                                    {Array.isArray(items) && items.map((row: any, index: number) => (
                                                                                        <TableRow key={`${row.primaryKey}-time-${row.publish_time}-${index}`}>
                                                                                            <TableCell sx={tableCellDateStyle}>{displayTime(row.publish_time)}</TableCell>
                                                                                            <TableCell sx={tableCellNameStyle}>{row.update_name}</TableCell>
                                                                                            <TableCell sx={tableCellSmallStyle}>{row.branch}</TableCell>
                                                                                            <TableCell sx={tableCellWhiteSpace}>{row.tableLogicName}</TableCell>
                                                                                            <TableCell sx={tableCellWhiteSpace}>{row.columnLogicName}</TableCell>
                                                                                            <TableCell sx={tableCellStyle}></TableCell>
                                                                                            <TableCell sx={tableCellStyle}></TableCell>
                                                                                            <TableCell sx={tableCellStyle}>{row.afterValueDisplay}</TableCell>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </React.Fragment>
                                                                            );
                                                                        })}
                                                                        
                                                                        {/* 通常項目：青い行で展開可能表示 */}
                                                                        {Object.entries(regularItems).map(([itemName, items]) => {
                                                                            const itemExpandKey = `${branchKey}_${pageName}_${itemName}`;
                                                                            const isItemExpanded = expandedItems[itemExpandKey];
                                                                            const itemFirstRecord = Array.isArray(items) && items.length > 0 ? items[0] : null;
                                                                            
                                                                            // 項目グループの集約データを取得
                                                                            const itemSummary = getItemGroupSummary(items);
                                                                            
                                                                            return (
                                                                                <React.Fragment key={`item-${itemExpandKey}`}>
                                                                                    {/* 項目グループヘッダー行 */}
                                                                                    <TableRow>
                                                                                        <TableCell sx={tableCellDateStyle}>
                                                                                            {itemFirstRecord ? displayTime(itemFirstRecord.publish_time) : ""}
                                                                                        </TableCell>
                                                                                        <TableCell sx={tableCellNameStyle}>
                                                                                            {itemFirstRecord ? itemFirstRecord.update_name : ""}
                                                                                        </TableCell>
                                                                                        <TableCell sx={tableCellSmallStyle}>
                                                                                            {itemFirstRecord ? itemFirstRecord.branch : ""}
                                                                                        </TableCell>
                                                                                        <TableCell sx={tableCellWhiteSpace}>
                                                                                            {itemFirstRecord ? itemFirstRecord.tableLogicName : ""}
                                                                                        </TableCell>
                                                                                        <TableCell 
                                                                                            sx={{
                                                                                                ...tableCellWhiteSpace,
                                                                                                backgroundColor: "#CCFFFF",
                                                                                                cursor: "pointer"
                                                                                            }}
                                                                                            onClick={() => handleItemGroupClick(branchKey, pageName, itemName)}
                                                                                        >
                                                                                            {itemName} {isItemExpanded ? "▲" : "▼"}
                                                                                        </TableCell>
                                                                                        <TableCell sx={tableCellStyle}>
                                                                                            {itemSummary ? itemSummary.oldestRecord.beforeValueDisplay : ""}
                                                                                        </TableCell>
                                                                                        <TableCell sx={tableCellStyle}>
                                                                                            {itemSummary ? itemSummary.newestRecord.afterValueDisplay : ""}
                                                                                        </TableCell>
                                                                                        <TableCell sx={tableCellStyle}>
                                                                                            {itemSummary ? change_details(itemSummary.latestRecord.log_type) : ""}
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                    
                                                                                    {/* 項目展開時の詳細レコード */}
                                                                                    {isItemExpanded && Array.isArray(items) && items.map((row: any, index: number) => (
                                                                                        <TableRow key={`${row.primaryKey}-time-${row.publish_time}-${index}`}>
                                                                                            <TableCell sx={tableCellDateStyle}>{displayTime(row.publish_time)}</TableCell>
                                                                                            <TableCell sx={tableCellNameStyle}>{row.update_name}</TableCell>
                                                                                            <TableCell sx={tableCellSmallStyle}>{row.branch}</TableCell>
                                                                                            <TableCell sx={tableCellWhiteSpace}>{row.tableLogicName}</TableCell>
                                                                                            <TableCell sx={tableCellWhiteSpace}>{row.columnLogicName}</TableCell>
                                                                                            <TableCell sx={tableCellStyle}>{row.beforeValueDisplay}</TableCell>
                                                                                            <TableCell sx={tableCellStyle}>{row.afterValueDisplay}</TableCell>
                                                                                            <TableCell sx={tableCellStyle}>{change_details(row.log_type)}</TableCell>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </React.Fragment>
                                                                            );
                                                                        })}
                                                                    </React.Fragment>
                                                                );
                                                            }
                                                        })()}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
            <ChangeHistoryTableComment
                props={{
                    open: commentOpen,
                    params: {
                        planAreaId: planAreaId,
                        branch: latestBranch,
                        comment: "",
                    },
                    pages: filterOptions.pages,
                    handleModal: setCommentOpen,
                    handleSaveComment: handleSaveComment,
                    handleCanCelComment: handleCanCelComment,
                }}
            />
            <FilterModal
                open={filterModalOpen}
                onClose={handleFilterClose}
                onApplyFilter={handleApplyFilter}
                filterOptions={filterOptions}
                currentFilters={filterCriteria}
            />
        </Box>
    );
};

export default ItemChangeHistory;
